import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const CategoryAnalytics = ({ data }) => {
  // Prepare data for pie chart
  const chartData = data?.map(item => ({
    name: item?.category,
    value: parseFloat(item?.total_value) || 0,
    products: item?.total_products,
    turnover: parseFloat(item?.avg_turnover_rate) || 0,
    lowStock: item?.low_stock_count,
    outOfStock: item?.out_of_stock_count
  }));

  // Colors for pie chart
  const COLORS = [
    'var(--color-primary)',
    'var(--color-secondary)',
    'var(--color-success)',
    'var(--color-warning)',
    'var(--color-error)',
    'var(--color-info)',
    'var(--color-accent)'
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="font-medium text-foreground mb-2">{data?.name}</p>
          <p className="text-sm text-muted-foreground">Total Value: ${data?.value?.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground">Products: {data?.products}</p>
          <p className="text-sm text-primary">Avg Turnover: {data?.turnover?.toFixed(2)}x</p>
          {data?.lowStock > 0 && (
            <p className="text-sm text-warning">Low Stock: {data?.lowStock}</p>
          )}
          {data?.outOfStock > 0 && (
            <p className="text-sm text-error">Out of Stock: {data?.outOfStock}</p>
          )}
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

    if (percent < 0.05) return null; // Don't show label for small slices

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100)?.toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-1">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-1">
            Category Analytics
          </h2>
          <p className="text-sm text-muted-foreground">
            Inventory value distribution by category
          </p>
        </div>
        <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
          <Icon name="PieChart" size={20} color="var(--color-secondary)" />
        </div>
      </div>
      {chartData?.length === 0 ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <Icon name="PieChart" size={48} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No category data available</p>
          </div>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={100}
                fill="var(--color-primary)"
                dataKey="value"
              >
                {chartData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Category Details Table */}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-2 text-muted-foreground font-medium">Category</th>
                  <th className="text-right py-2 px-2 text-muted-foreground font-medium">Products</th>
                  <th className="text-right py-2 px-2 text-muted-foreground font-medium">Value</th>
                  <th className="text-right py-2 px-2 text-muted-foreground font-medium">Turnover</th>
                  <th className="text-right py-2 px-2 text-muted-foreground font-medium">Issues</th>
                </tr>
              </thead>
              <tbody>
                {chartData?.map((item, index) => (
                  <tr key={item?.name} className="border-b border-border/50 hover:bg-muted/5">
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                        ></div>
                        <span className="font-medium text-foreground">{item?.name}</span>
                      </div>
                    </td>
                    <td className="text-right py-2 px-2 text-foreground">{item?.products}</td>
                    <td className="text-right py-2 px-2 text-foreground font-medium">
                      ${item?.value?.toLocaleString()}
                    </td>
                    <td className="text-right py-2 px-2">
                      <span className={`font-medium ${
                        item?.turnover >= 2 ? 'text-success' :
                        item?.turnover >= 1 ? 'text-warning' : 'text-error'
                      }`}>
                        {item?.turnover?.toFixed(2)}x
                      </span>
                    </td>
                    <td className="text-right py-2 px-2">
                      {(item?.lowStock > 0 || item?.outOfStock > 0) ? (
                        <div className="flex items-center justify-end gap-1">
                          {item?.outOfStock > 0 && (
                            <span className="px-2 py-0.5 bg-error/10 text-error rounded text-xs font-medium">
                              {item?.outOfStock} out
                            </span>
                          )}
                          {item?.lowStock > 0 && (
                            <span className="px-2 py-0.5 bg-warning/10 text-warning rounded text-xs font-medium">
                              {item?.lowStock} low
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-success text-xs">✓ Good</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryAnalytics;